import React, { useState, useEffect, useRef } from "react";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import "./App.scss";
import "../styles/sharedStyles.scss";
import PreviewTermsConditions from "../shared/previewTermsConditions/PreviewTermsConditions";
import TermsDeclined from "../components/declineTerms/TermsDeclined";
import Dealer from "./pure/Dealer";
import Help from "../components/help/help";
import GlobalNavBar from "../shared/globalNavBar/GlobalNavBar";
import Footer from "../shared/Footer/Footer";
import QuickStart from "../components/quickStart/QuickStart";
import ProgressBarTabs from "../components/progressBar/ProgressBarTabs";
import AccessDeniedAsBevDisabled from "../components/accessDenied/AccessDeniedAsBevDisabled";
import AccessDeniedAsNotOnBoarded from "../components/accessDenied/AccessDeniedAsNotOnBoarded";
import InvalidRoute from "../components/accessDenied/InvalidRoute";
import BusinessSettings from "../components/businessSetting/BusinessSettings";
import DealerDashboard from "../components/dashboard/dealerDashBoard/DealerDashboard";
import { logMsg } from "../common/Logger";
import withIdleTimer from "../common/WithIdleTimer";
import DealerPricing from "../components/pricing/pricingWrapper/dealerPricing/DealerPricing";
import HasPermissionTo from "../common/HasPermissionTo";
import DealerOnboardingClient from "../shared/clients/DealerOnboardingClient";
import LocalizedInfoClient from "../shared/clients/LocalizedInfoClient";
import DealerGroupingClient from "../shared/clients/DealerGroupingClient";
import PendingActionClient from "../shared/clients/PendingActionClient";
import ErrorScreen from "../errors/ErrorScreen";
import AccessDeniedAsNscNotOnboarded from "../components/accessDenied/AccessDeniedAsNscNotOnboarded";
import { IS_SA_MARKET } from "../utils/EmpUtil";
import NscOnboardingClient from "../shared/clients/NscOnboardingClient";
import Nsc from "./pure/Nsc";

const DealerApp = (props) => {
  const [renderApp, setRenderApp] = useState(false);
  const [shouldNavigationBeRestricted, setShouldNavigationBeRestricted] =
    useState(false);
  const [dealerGroup, setDealerGroup] = useState([]);
  const [dealerGroupFiltered, setDealerGroupFiltered] = useState([]);
  const [currentDealer, setCurrentDealer] = useState(
    new Dealer(props.userDetails, props.history)
  );
  const [padding, setPaddingState] = useState("70px");
  const [dealerUpdateKey, setDealerUpdateKey] = useState(0);

  const localizedInfoRef = useRef({});
  const langRef = useRef(undefined);
  const onBoardingStatusRef = useRef(null);
  const dealerActiveStatusRef = useRef(null);
  const userRef = useRef(new Nsc(props.userDetails, props.history));

  useEffect(() => {
    getLocalizedInfo();

    const unlisten = props.history.listen(() => setNavigationRestriction());

    return () => {
      unlisten();
    };
  }, []);

  const onDealerDecline = () => {
    onBoardingStatusRef.current = "PENDING";
    navigateToRoute("");
  };

  const onReservationSave = () => {
    onBoardingStatusRef.current = "ACCEPTED";
    window.location.href = "/";
  };

  const onDecline = () => {
    onBoardingStatusRef.current = "DECLINED";
    navigateToRoute("declined");
  };

  const getLocalizedInfo = () => {
    LocalizedInfoClient.getLocalizedInfo(currentDealer)
      .then((response) => {
        localizedInfoRef.current = Object.assign(
          {},
          { ...localizedInfoRef.current },
          { ...response }
        );
        langRef.current = localizedInfoRef.current.defaultLanguage.isocode;

        props.i18n.changeLanguage(
          langRef.current.substring(0, 2) + "_" + props.userDetails.market
        );
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        hasNscOnboarded();
      });
  };

  const isBevEnabledAndOnboarded = () => {
    props.hideOrShow(true);
    DealerGroupingClient.getDealerGroup(currentDealer)
      .then((data) => {
        let dealer;
        if (data.length > 1) {
          dealer = data.filter(
            (dealer) =>
              dealer.dealerType === "MDLR" || dealer.dealerType === "HUB"
          )[0];
        } else {
          dealer = data[0];
        }

        const bevAndIceDealers = data.sort((a, b) => {
          if (a.dealerType === "MDLR" || a.dealerType === "HUB") {
            return -1;
          } else if (b.dealerType === "MDLR" || b.dealerType === "HUB") {
            return 1;
          } else {
            return 0;
          }
        });

        if (dealer.dealerType === "MDLR" || dealer.dealerType === "HUB") {
          setDealerGroup(bevAndIceDealers);
          setDealerGroupFiltered(
            bevAndIceDealers.filter((dealer) => dealer.dealerType !== "SDLR")
          );
        } else {
          setDealerGroup(bevAndIceDealers);
          setDealerGroupFiltered(bevAndIceDealers);
        }

        currentDealer.setDealerGroup(bevAndIceDealers);
        updateCurrentDealer(dealer, true);
        onBoardingStatusRef.current = dealer.onBoardingStatus;
        dealerActiveStatusRef.current = dealer.dealerActiveStatus;

        determinePendingActionStatuses();
      })
      .catch(() => {
        performFinalActions();
      });
  };

  const determinePendingActionStatuses = () => {
    PendingActionClient.getHasNscCompletedPA(currentDealer, 1)
      .then((hasNscCompletedBasicPAs) => {
        currentDealer.hasNscCompletedBasicPAs = hasNscCompletedBasicPAs;
        setCurrentDealer(currentDealer);
        setDealerUpdateKey((prev) => prev + 1);
      })
      .catch((error) => {
        console.error("Error getting NSC completion status:", error);
      })
      .finally(() => {
        setTimeout(() => {
          getPendingActions().finally(() => performFinalActions());
        }, 100);
      });
  };

  const hasNscOnboarded = () => {
    NscOnboardingClient.isOnboarded(userRef.current).then((hasNscOnboarded) => {
      if (hasNscOnboarded.onboarded) {
        isBevEnabledAndOnboarded();
      } else {
        onBoardingStatusRef.current = "NSC_NOT_ONBOARDED";
        navigateToRoute("nsc-not-onboarded");
        performFinalActions();
      }
    });
  };

  const performFinalActions = () => {
    setNavigationRestriction();
    props.hideOrShow(false);
    setRenderApp(true);
  };

  const setNavigationRestriction = () => {
    if (onBoardingStatusRef.current !== "ACCEPTED") {
      setShouldNavigationBeRestricted(true);
    } else {
      const isAllowedPath = isCurrentPathAllowedToShowHeaderAndFooter(
        props.history.location.pathname
      );
      setShouldNavigationBeRestricted(!isAllowedPath);
    }
  };

  const isCurrentPathAllowedToShowHeaderAndFooter = (currentPathName) => {
    const paths = [
      "/help",
      "/business-settings",
      "/dashboard",
      "/pricing/dealerpricing",
      "/pricing",
      "/",
    ];

    return paths.includes(currentPathName);
  };

  const navigateToRoute = (link) => {
    props.history.push(`/${link}`);
  };

  const updateDealerPhoneAndEmail = (dealer, email, phone) => {
    const index = dealerGroup.indexOf(
      dealerGroup.filter((d) => d.completeDealerId === dealer.dealerId)[0]
    );

    const dealerGroupCopy = JSON.parse(JSON.stringify(dealerGroup));
    dealerGroupCopy[index].emailAddress = email;
    dealerGroupCopy[index].phoneNumber = phone;

    setDealerGroup(dealerGroupCopy);
  };

  const updateDealerEmpMembership = (dealer) => {
    const index = dealerGroup.indexOf(
      dealerGroup.filter(
        (d) => d.completeDealerId === dealer.completeDealerId
      )[0]
    );

    const updatedDealerGroup = [...dealerGroup];
    updatedDealerGroup[index].status = dealer.status;

    setDealerGroup(updatedDealerGroup);
  };

  const isMainViewingSDLR = (dealer) => {
    return currentDealer.isLoggedInUserMDLR && dealer.dealerType === "SDLR";
  };

  const getPendingActions = async (
    dealer = currentDealer,
    updatePAOnly = true
  ) => {
    if (!dealer || !dealer.dealerGroup || dealer.dealerGroup.length === 0) {
      console.warn("Dealer data not ready for pending actions API call");
      return;
    }

    props.hideOrShow(true);
    await PendingActionClient.getDealerPendingActionsNotifications(
      dealer,
      false
    )
      .then((data) => {
        props.hideOrShow(false);
        dealer.updatePendingActions(data.pendingActions);

        if (!updatePAOnly) {
          setCurrentDealer(dealer);
        }
        setDealerUpdateKey((prev) => prev + 1);
      })
      .catch((error) => {
        props.hideOrShow(false);
        const updatedDealer = updatePAOnly ? currentDealer : dealer;
        updatedDealer.updatePendingActions([]);
        setCurrentDealer(updatedDealer);
        setDealerUpdateKey((prev) => prev + 1);
        logMsg("the error is: ", error);
      });
  };

  const updateCurrentDealer = (
    dealer,
    showAll = false,
    getPendingActionsFlag = false
  ) => {
    const newDealer = currentDealer.updateCurrentDealer(dealer, showAll);
    const hasPendingActionPermission =
      currentDealer.permissions.rolePermissions.hasOwnProperty([
        "pendingActionQueue:view",
      ]);
    if (getPendingActionsFlag && hasPendingActionPermission) {
      getPendingActions(newDealer, false);
    } else {
      setCurrentDealer(newDealer);
      setDealerUpdateKey((prev) => prev + 1);
    }
  };

  const dealerChanged = (dealer, showAll = false) => {
    updateCurrentDealer(dealer, showAll, true);
  };

  const setPadding = (paddingValue) => {
    if (paddingValue > 70) {
      setPaddingState(paddingValue + "px");
    }
  };

  const getProgressBarTabs = () => {
    return IS_SA_MARKET(userRef.current.market)
      ? ProgressBarTabs().onboardingSADealer
      : ProgressBarTabs().onboardingDealer;
  };

  const ProtectedRoute = ({
    component: Component,
    perform: perform,
    ...rest
  }) => (
    <HasPermissionTo
      perform={perform}
      permissions={currentDealer.permissions.rolePermissions}
      render={() => (
        <Route
          {...rest}
          render={(routeProps) =>
            onBoardingStatusRef.current === "ACCEPTED" ? (
              <Component
                {...rest.customprops}
                {...props}
                {...routeProps.match.params}
              />
            ) : (
              <Redirect to="/" />
            )
          }
        />
      )}
    />
  );

  const DeclinedRoute = ({ component: Component, ...rest }) => (
    <Route
      {...rest}
      render={() =>
        onBoardingStatusRef.current === "DECLINED" ? (
          <Component {...rest.customprops} {...props} />
        ) : (
          <Redirect to="/" />
        )
      }
    />
  );

  return renderApp ? (
    <Router history={props.history}>
      <div className="appWrapper" id="appWrapper">
        {shouldNavigationBeRestricted ? null : (
          <div style={{ paddingTop: padding }}>
            <GlobalNavBar
              key={dealerUpdateKey}
              {...props}
              dealerGroup={dealerGroup}
              onDealerChanged={dealerChanged}
              user={currentDealer}
              urlPath={window.location.pathname}
              setPadding={setPadding}
              navigateToRoute={navigateToRoute}
            />
          </div>
        )}
        <Switch>
          <ProtectedRoute
            exact
            path="/dashboard"
            component={withIdleTimer(DealerDashboard)}
            perform={["dashBoardPage"]}
            customprops={{
              user: currentDealer,
              isMainViewingSDLR: isMainViewingSDLR,
              onDealerChanged: dealerChanged,
              lang: langRef.current,
            }}
          />
          <ProtectedRoute
            exact
            path="/business-settings"
            component={withIdleTimer(BusinessSettings)}
            perform={["businessSettingsPage"]}
            customprops={{
              isMainViewingSDLR: isMainViewingSDLR,
              getPendingActions: getPendingActions,
              user: currentDealer,
              lang: langRef.current,
              dealerGroup: dealerGroup,
              localizedInfo: localizedInfoRef.current,
              updateDealerGroupDetails: isBevEnabledAndOnboarded,
              updateDealerPhoneAndEmail: updateDealerPhoneAndEmail,
              updateDealerEmpMembership: updateDealerEmpMembership,
              onDealerChanged: dealerChanged,
              permissions: props.userDetails.permissions,
            }}
          />
          <Route
            exact
            path="/help"
            component={withIdleTimer(() => (
              <Help {...props} market={currentDealer.market} />
            ))}
          />
          <DeclinedRoute
            exact
            path="/declined"
            component={withIdleTimer(TermsDeclined)}
            customprops={{
              user: currentDealer,
              onDecline: onDealerDecline,
            }}
          />
          <ProtectedRoute
            exact
            path="/pricing/dealerpricing"
            perform={["dealerPricingTab"]}
            component={withIdleTimer(() => (
              <DealerPricing
                {...props}
                localizedInfo={localizedInfoRef.current}
                user={currentDealer}
                isMainViewingSDLR={isMainViewingSDLR}
                onDealerChanged={dealerChanged}
              />
            ))}
          />

          <Route exact path="/denied" component={AccessDeniedAsBevDisabled} />
          <Route
            exact
            path="/notonboarded"
            component={AccessDeniedAsNotOnBoarded}
          />
          <Route
            exact
            path="/nsc-not-onboarded"
            component={AccessDeniedAsNscNotOnboarded}
          />
          <Route
            exact
            path="/"
            render={() =>
              onBoardingStatusRef.current === "ACCEPTED" ||
              (onBoardingStatusRef.current === "DECLINED" &&
                dealerActiveStatusRef.current === "IN_ACTIVE") ? (
                <Redirect
                  to={{
                    pathname: "/dashboard",
                  }}
                />
              ) : onBoardingStatusRef.current === "DECLINED" ? (
                <Redirect
                  to={{
                    pathname: "/declined",
                  }}
                />
              ) : onBoardingStatusRef.current === "ONBOARDINGNOTCOMPLETED" ? (
                <Redirect
                  to={{
                    pathname: "/notonboarded",
                  }}
                />
              ) : (
                onBoardingStatusRef.current === "PENDING" && (
                  <QuickStart
                    {...props}
                    user={currentDealer}
                    isMainViewingSDLR={isMainViewingSDLR}
                    dealerGroup={dealerGroupFiltered}
                    history={props.history}
                    onboardingClient={DealerOnboardingClient}
                    localizedInfo={localizedInfoRef.current}
                    onSave={onReservationSave}
                    onDecline={onDecline}
                    progressBarTabs={getProgressBarTabs()}
                    Preview={PreviewTermsConditions}
                    fromQuickstart={true}
                  />
                )
              )
            }
          />
          <Route
            exact
            path="/error"
            render={(routeProps) => <ErrorScreen {...routeProps} />}
          />
          <Route component={withIdleTimer(InvalidRoute)} />
        </Switch>
        {shouldNavigationBeRestricted ? null : (
          <Footer permissions={props.userDetails.permissions} />
        )}
      </div>
    </Router>
  ) : null;
};

export default DealerApp;
